/**
 * Settings › Websites' own API calls.
 *
 * Kept local to this feature rather than added to `@/lib/api`, for the same
 * reason the domains and users screens' are: a feature owns the endpoints
 * only it speaks to, so a session's PR touches no file another session also
 * owns.
 *
 * `SiteCredentialsBackend` is the seam the demo client plugs into:
 * `siteCredentialsBackendFor` hands back the mock's in-memory store when the
 * app is running on the demo client, so the screen is developable and
 * testable with no API at all.
 *
 * Issue never caches: the API answers `Cache-Control: no-store` because the
 * token is shown once and never retrievable again, and this client mirrors
 * that by never putting the response in React Query's cache — the page keeps
 * it in component state only, for exactly as long as it is on screen.
 */

import type { ApiClient } from '@/api/client'
import { ApiError, type ApiErrorBody } from '@/lib/api'

import type {
  IssuedSiteCredential,
  SiteCredential,
  SiteCredentialKind,
  SiteCredentialListOut,
} from './types'

const BASE = '/api/v1/websites'

export interface SiteCredentialsBackend {
  list(websiteId: string): Promise<SiteCredential[]>
  /** The token is in this response only; it is never shown again. */
  issue(websiteId: string, label: string, kind?: SiteCredentialKind): Promise<IssuedSiteCredential>
  /** Idempotent: revoking an already-revoked credential changes nothing. */
  revoke(websiteId: string, credentialId: string): Promise<SiteCredential>
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'same-origin',
    // Every response here can carry a credential's metadata or its one-time
    // token; the browser's own HTTP cache must never hold either.
    cache: 'no-store',
    headers: init?.body ? { 'Content-Type': 'application/json', ...(init?.headers ?? {}) } : init?.headers,
  })
  if (!response.ok) {
    let body: ApiErrorBody | undefined
    try {
      body = (await response.json()) as ApiErrorBody
    } catch {
      body = undefined
    }
    throw new ApiError(
      response.status,
      body?.error.code ?? 'temporary_failure',
      body?.error.message ?? response.statusText,
      body?.correlation_id,
    )
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const siteCredentialsApi: SiteCredentialsBackend = {
  list: async (websiteId) => {
    const { items } = await request<SiteCredentialListOut>(
      `/${encodeURIComponent(websiteId)}/site-credentials`,
    )
    return items
  },
  issue: (websiteId, label, kind = 'ingest') =>
    request<IssuedSiteCredential>(`/${encodeURIComponent(websiteId)}/site-credentials`, {
      method: 'POST',
      body: JSON.stringify({ label, kind }),
    }),
  revoke: (websiteId, credentialId) =>
    request<SiteCredential>(
      `/${encodeURIComponent(websiteId)}/site-credentials/${encodeURIComponent(credentialId)}/revoke`,
      { method: 'POST' },
    ),
}

interface WithSiteCredentials {
  readonly siteCredentials: SiteCredentialsBackend
}

function hasSiteCredentials(client: ApiClient): client is ApiClient & WithSiteCredentials {
  const value = (client as Partial<WithSiteCredentials>).siteCredentials
  return typeof value === 'object' && value !== null
}

/** The demo client's in-memory store when there is one, the real API otherwise. */
export function siteCredentialsBackendFor(client: ApiClient): SiteCredentialsBackend {
  return hasSiteCredentials(client) ? client.siteCredentials : siteCredentialsApi
}
